import {useNavigate, useParams} from 'react-router-dom'
import React, {useState} from 'react'
import {IntervencijeOdrzavanja} from '../../../models/IntervencijeOdrzavanja'
import {saveIntervencija} from '../../../api/intervencijeOdrzavanja'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import notifyToast from '../../../components/Toast/Toast'
import {ToastContainer} from 'react-toastify'
import TitleComponent from '../../../components/TitleComponent/TitleComponent'
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent'
import PrijavaKvaraDropdown from '../../../components/PrijavaKvaraDropdown/PrijavaKvaraDropdown'
import clsx from 'clsx'
import UposlenikDropdown from '../../../components/UposlenikDropdown/UposlenikDropdown'
import {KTIcon} from '../../../../_websoft/helpers'
import StatusIntervencijeDropdown from '../../../components/StatusDropdown/StatusIntervencijeDropdown'
import {useAuth} from '../../../modules/auth'
import SmjenePanelaDropdown from '../../paneli/components/SmjenePanelaDropdown/SmjenePanelaDropdown'

const DodajIntervenciju: React.FC = () => {
    const {id} = useParams()
    const IdFromURL = Number(id)

    const [loading, setLoading] = useState(false)
    const {currentUser} = useAuth()
    const navigate = useNavigate()

    // ✅ UI helpers
    const sectionCard = 'card mb-8 border-0 shadow-sm'
    const sectionBody = 'card-body p-5 p-lg-7'
    const labelClass = 'form-label fw-bold mb-2'
    const helpClass = 'text-muted small'
    const controlClass = 'form-control form-control-lg form-control-solid bg-light-subtle'

    // ✅ Field error renderer (works with simple fields)
    const FieldError = ({name}: {name: keyof any}) => {
        const touched = (formik.touched as any)[name]
        const error = (formik.errors as any)[name]
        if (!touched || !error) return null
        return (
            <div className='fv-plugins-message-container mt-2'>
                <div className='fv-help-block text-danger fw-semibold small'>{error}</div>
            </div>
        )
    }

    // ✅ IMPORTANT: Izvrsilac is IDs => number[]
    const initialValues: Partial<IntervencijeOdrzavanja> = {
        Id: undefined,
        BrojIntervencije: IdFromURL?.toString?.() ?? '',
        Datum: undefined,
        DatumKraj: undefined,
        OpisRadova: undefined,
        Status: undefined,

        // FIX: number[] not string[]
        Izvrsilac: [] as (number | undefined)[],

        Voditelj: currentUser?.UposlenikId?.toString?.(),
        UtroseniMaterijal: undefined,
        UtrosenoVrijemeSati: undefined,
        UtrosenoVrijemeMinute: undefined,
        KoristenAlat: undefined,
        IzmjenaUlja: undefined,
        TipUlja: undefined,
        KolicinaUlja: undefined,
        RadniSati: undefined,
        SljedecaIzmjena: undefined,
        Napomena: undefined,
        RazlogKvara: undefined,
        RazlogKvaraDodatno: undefined,
        KvarNapravilaSmjena: undefined,
        KvarNapravioUposlenik: undefined,
    }

    const validationSchema = Yup.object({
        OpisRadova: Yup.string().required('Required'),
        UtrosenoVrijemeMinute: Yup.string().required('Required'),
        Status: Yup.string().required('Required'),
        Voditelj: Yup.string().required('Required'),
    })

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true)
            try {
                const response = await saveIntervencija(values)
                if (response.error === 0) {
                    notifyToast(true, response.message)
                    setTimeout(() => navigate(-1), 3000)
                } else {
                    notifyToast(false, response.message || 'An unexpected error occurred.')
                }
            } catch (error: any) {
                console.error('Error: ', error)
                notifyToast(false, error?.response?.data?.message)
                setStatus(error)
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })

    return (
        <div className='card mb-5 mb-xl-10 border-0 shadow-sm'>
            <ToastContainer />

            {/* HEADER */}
            <div className='card-header border-0 py-5'>
                <div className='d-flex align-items-start align-items-md-center justify-content-between w-100 flex-column flex-md-row gap-3'>
                    <div className='d-flex align-items-center gap-3'>
            <span className='symbol symbol-45px symbol-circle bg-light-primary'>
              <span className='symbol-label'>
                <KTIcon iconName='setting-2' className='fs-2 text-primary' />
              </span>
            </span>
                        <div>
                            <div className='d-flex align-items-center gap-2 flex-wrap'>
                                <div className='m-0'>
                                    <TitleComponent name='Intervenciju' />
                                </div>
                                <span className='badge badge-light-primary'>Unos</span>
                            </div>
                            <div className={helpClass}>Popunite podatke. Obavezna polja su označena zvjezdicom (*).</div>
                        </div>
                    </div>

                    <button type='button' className='btn btn-light-primary' onClick={() => navigate(-1)} disabled={loading}>
                        <KTIcon iconName='arrow-left' className='fs-3 me-2' />
                        Nazad
                    </button>
                </div>
            </div>

            <div className='card-body pt-0'>
                {loading ? (
                    <LoadingComponent />
                ) : (
                    <form onSubmit={formik.handleSubmit} noValidate>
                        {/* BOX 1: OSNOVNO */}
                        <div className={sectionCard}>
                            <div className='card-header border-0 py-4'>
                                <div className='card-title m-0 w-100'>
                                    <div className='d-flex align-items-start justify-content-between w-100 flex-column flex-md-row gap-2'>
                                        <div className='d-flex align-items-center gap-2'>
                                            <KTIcon iconName='profile-circle' className='fs-2 text-gray-700' />
                                            <h3 className='m-0 fs-4 fw-bold'>Osnovno</h3>
                                        </div>
                                        <div className={helpClass}>Unesite prijavu i vremenski period intervencije.</div>
                                    </div>
                                </div>
                            </div>

                            <div className={sectionBody}>
                                <div className='row g-6'>
                                    {/* Prijava Id */}
                                    <div className='col-12 col-lg-6'>
                                        <label className={clsx(labelClass, 'required')}>Prijava Id</label>
                                        <PrijavaKvaraDropdown
                                            value={formik.values.BrojIntervencije as any}
                                            onChange={(name, value) => formik.setFieldValue('BrojIntervencije ', value)}
                                            errors={formik.errors.BrojIntervencije as any}
                                            touched={formik.touched.BrojIntervencije as any}
                                            selectName='BrojIntervencije '
                                        />
                                        <FieldError name={'BrojIntervencije' as any} />
                                    </div>

                                    {/* Datum pocetak */}
                                    <div className='col-12 col-md-6 col-lg-3'>
                                        <label className={clsx(labelClass, 'required')}>Datum (početak)</label>
                                        <input
                                            type='date'
                                            {...formik.getFieldProps('Datum')}
                                            className={clsx(
                                                controlClass,
                                                {'is-invalid': formik.touched.Datum && formik.errors.Datum},
                                                {'is-valid': formik.touched.Datum && !formik.errors.Datum}
                                            )}
                                        />
                                        <FieldError name={'Datum' as any} />
                                    </div>

                                    {/* Datum kraj */}
                                    <div className='col-12 col-md-6 col-lg-3'>
                                        <label className={clsx(labelClass, 'required')}>Datum (kraj)</label>
                                        <input
                                            type='date'
                                            {...formik.getFieldProps('DatumKraj')}
                                            className={clsx(
                                                controlClass,
                                                {'is-invalid': formik.touched.DatumKraj && formik.errors.DatumKraj},
                                                {'is-valid': formik.touched.DatumKraj && !formik.errors.DatumKraj}
                                            )}
                                        />
                                        <FieldError name={'DatumKraj' as any} />
                                    </div>

                                    {/* Utrošeno vrijeme */}
                                    <div className='col-12'>
                                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2'>
                                            <label className={clsx(labelClass, 'required m-0')}>Utrošeno vrijeme</label>
                                            <span className={helpClass}>Unesite sate i minute (npr. 2h 30min)</span>
                                        </div>

                                        <div className='row g-4'>
                                            <div className='col-12 col-md-6 col-lg-3'>
                                                <div className='input-group input-group-lg'>
                                                    <span className='input-group-text bg-light-subtle'>Sati</span>
                                                    <input
                                                        type='text'
                                                        placeholder='0'
                                                        {...formik.getFieldProps('UtrosenoVrijemeSati')}
                                                        className={clsx(
                                                            controlClass,
                                                            {
                                                                'is-invalid':
                                                                    formik.touched.UtrosenoVrijemeSati && formik.errors.UtrosenoVrijemeSati,
                                                            },
                                                            {
                                                                'is-valid':
                                                                    formik.touched.UtrosenoVrijemeSati && !formik.errors.UtrosenoVrijemeSati,
                                                            }
                                                        )}
                                                    />
                                                </div>
                                                <FieldError name={'UtrosenoVrijemeSati' as any} />
                                            </div>

                                            <div className='col-12 col-md-6 col-lg-3'>
                                                <div className='input-group input-group-lg'>
                                                    <span className='input-group-text bg-light-subtle'>Minute *</span>
                                                    <input
                                                        type='text'
                                                        placeholder='0'
                                                        {...formik.getFieldProps('UtrosenoVrijemeMinute')}
                                                        className={clsx(
                                                            controlClass,
                                                            {
                                                                'is-invalid':
                                                                    formik.touched.UtrosenoVrijemeMinute && formik.errors.UtrosenoVrijemeMinute,
                                                            },
                                                            {
                                                                'is-valid':
                                                                    formik.touched.UtrosenoVrijemeMinute && !formik.errors.UtrosenoVrijemeMinute,
                                                            }
                                                        )}
                                                    />
                                                </div>
                                                <FieldError name={'UtrosenoVrijemeMinute' as any} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* BOX 2: OPIS I ALAT */}
                        <div className={sectionCard}>
                            <div className='card-header border-0 py-4'>
                                <div className='card-title m-0 w-100'>
                                    <div className='d-flex align-items-start justify-content-between w-100 flex-column flex-md-row gap-2'>
                                        <div className='d-flex align-items-center gap-2'>
                                            <KTIcon iconName='note' className='fs-2 text-gray-700' />
                                            <h3 className='m-0 fs-4 fw-bold'>Opis i alat</h3>
                                        </div>
                                        <div className={helpClass}>Kratko opišite radove i ko je voditelj.</div>
                                    </div>
                                </div>
                            </div>

                            <div className={sectionBody}>
                                <div className='row g-6'>
                                    <div className='col-12 col-lg-7'>
                                        <label className={clsx(labelClass, 'required')}>Opis radova</label>
                                        <textarea
                                            rows={6}
                                            placeholder='Kratko opišite šta je rađeno...'
                                            {...formik.getFieldProps('OpisRadova')}
                                            className={clsx(
                                                controlClass,
                                                'resize-none',
                                                {'is-invalid': formik.touched.OpisRadova && formik.errors.OpisRadova},
                                                {'is-valid': formik.touched.OpisRadova && !formik.errors.OpisRadova}
                                            )}
                                        />
                                        <FieldError name={'OpisRadova' as any} />
                                    </div>

                                    <div className='col-12 col-lg-5'>
                                        <div className='mb-6'>
                                            <label className={clsx(labelClass, 'required')}>Voditelj</label>
                                            <UposlenikDropdown
                                                value={formik.values.Voditelj as any}
                                                onChange={(name, value) => formik.setFieldValue('Voditelj', value)}
                                                errors={formik.errors.Voditelj as any}
                                                touched={formik.touched.Voditelj as any}
                                                selectName='Voditelj'
                                            />
                                            <FieldError name={'Voditelj' as any} />
                                        </div>

                                        <div>
                                            <label className={labelClass}>Korišten alat</label>
                                            <textarea
                                                rows={4}
                                                placeholder='Npr. ključevi, bušilica ...'
                                                {...formik.getFieldProps('KoristenAlat')}
                                                className={clsx(
                                                    controlClass,
                                                    'resize-none',
                                                    {'is-invalid': formik.touched.KoristenAlat && formik.errors.KoristenAlat},
                                                    {'is-valid': formik.touched.KoristenAlat && !formik.errors.KoristenAlat}
                                                )}
                                            />
                                            <FieldError name={'KoristenAlat' as any} />
                                        </div>
                                    </div>

                                    <div className='col-12 col-lg-4'>
                                        <label className={labelClass}>Zamjena ulja</label>
                                        <select className={clsx(controlClass, 'form-select')} {...formik.getFieldProps('IzmjenaUlja')}>
                                            <option value=''>Odaberi...</option>
                                            <option value='1'>Da</option>
                                            <option value='0'>Ne</option>
                                        </select>
                                        <FieldError name={'IzmjenaUlja' as any} />
                                        <div className={helpClass + ' mt-2'}>Ako je “Da”, pojavit će se dodatna polja.</div>
                                    </div>

                                    {formik.values.IzmjenaUlja === '1' && (
                                        <>
                                            <div className='col-12 col-md-6 col-lg-4'>
                                                <label className={clsx(labelClass, 'required')}>Tip ulja</label>
                                                <input type='text' {...formik.getFieldProps('TipUlja')} className={controlClass} />
                                                <FieldError name={'TipUlja' as any} />
                                            </div>

                                            <div className='col-12 col-md-6 col-lg-4'>
                                                <label className={clsx(labelClass, 'required')}>Količina</label>
                                                <input type='text' {...formik.getFieldProps('KolicinaUlja')} className={controlClass} />
                                                <FieldError name={'KolicinaUlja' as any} />
                                            </div>

                                            <div className='col-12 col-md-6 col-lg-4'>
                                                <label className={clsx(labelClass, 'required')}>Radni sati</label>
                                                <input type='text' {...formik.getFieldProps('RadniSati')} className={controlClass} />
                                                <FieldError name={'RadniSati' as any} />
                                            </div>

                                            <div className='col-12 col-md-6 col-lg-4'>
                                                <label className={clsx(labelClass, 'required')}>Sljedeća izmjena</label>
                                                <input
                                                    type='date'
                                                    placeholder='Sljedeća izmjena'
                                                    {...formik.getFieldProps('SljedecaIzmjena')}
                                                    className={clsx(
                                                        controlClass,
                                                        {'is-invalid': formik.touched.SljedecaIzmjena && formik.errors.SljedecaIzmjena},
                                                        {'is-valid': formik.touched.SljedecaIzmjena && !formik.errors.SljedecaIzmjena}
                                                    )}
                                                />
                                                <FieldError name={'SljedecaIzmjena' as any} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* BOX 3: IZVRŠIOCI I STATUS */}
                        <div className={sectionCard}>
                            <div className='card-header border-0 py-4'>
                                <div className='card-title m-0 w-100'>
                                    <div className='d-flex align-items-start justify-content-between w-100 flex-column flex-md-row gap-2'>
                                        <div className='d-flex align-items-center gap-2'>
                                            <KTIcon iconName='people' className='fs-2 text-gray-700' />
                                            <h3 className='m-0 fs-4 fw-bold'>Izvršioci i status</h3>
                                        </div>
                                        <div className={helpClass}>Dodajte osobe koje su radile i odaberite status.</div>
                                    </div>
                                </div>
                            </div>

                            <div className={sectionBody}>
                                <div className='row g-6'>
                                    {/* Izvrsilac */}
                                    <div className='col-12 col-lg-7'>
                                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2'>
                                            <label className={clsx(labelClass, 'required m-0')}>Izvršioci</label>

                                            <button
                                                type='button'
                                                className='btn btn-sm btn-light-primary'
                                                onClick={() => {
                                                    const al: (number | undefined)[] = [
                                                        ...(((formik.values.Izvrsilac as any) ?? []) as (number | undefined)[]),
                                                    ]
                                                    al.push(undefined)
                                                    formik.setFieldValue('Izvrsilac', al)
                                                }}
                                            >
                                                <KTIcon iconName='plus' className='fs-3 me-2' />
                                                Dodaj izvršioca
                                            </button>
                                        </div>

                                        <div className='d-flex flex-column gap-4'>
                                            {(((formik.values.Izvrsilac as any) ?? []) as (number | undefined)[]).map((val, i) => (
                                                <div key={i} className='row g-3 align-items-start'>
                                                    <div className='col-12 col-md-9'>
                                                        <UposlenikDropdown
                                                            value={val as any}
                                                            onChange={(name, value) => {
                                                                const al: (number | undefined)[] = [
                                                                    ...(((formik.values.Izvrsilac as any) ?? []) as (number | undefined)[]),
                                                                ]
                                                                al[i] = value ? Number(value) : undefined
                                                                formik.setFieldValue('Izvrsilac', al)
                                                            }}
                                                            // array field -> dropdown expects scalar error/touched, so keep undefined
                                                            errors={undefined as any}
                                                            touched={undefined as any}
                                                            selectName='Izvrsilac'
                                                        />
                                                    </div>

                                                    <div className='col-12 col-md-3 d-grid'>
                                                        <button
                                                            type='button'
                                                            className='btn btn-light-danger'
                                                            onClick={() => {
                                                                const al: (number | undefined)[] = [
                                                                    ...(((formik.values.Izvrsilac as any) ?? []) as (number | undefined)[]),
                                                                ]
                                                                al.splice(i, 1)
                                                                formik.setFieldValue('Izvrsilac', al)
                                                            }}
                                                        >
                                                            <KTIcon iconName='minus' className='fs-2 me-2' />
                                                            Ukloni
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}

                                            {(!formik.values.Izvrsilac || (formik.values.Izvrsilac as any)?.length === 0) && (
                                                <div className='p-4 bg-light rounded'>
                                                    <div className='d-flex align-items-center gap-2'>
                                                        <KTIcon iconName='information-5' className='fs-2 text-gray-600' />
                                                        <div>
                                                            <div className='fw-semibold'>Nema izvršilaca</div>
                                                            <div className={helpClass}>Kliknite “Dodaj izvršioca” da dodate osobu.</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Komentar + Status */}
                                    <div className='col-12 col-lg-5'>
                                        <div className='mb-6'>
                                            <label className={labelClass}>Komentar</label>
                                            <textarea
                                                rows={4}
                                                placeholder='Dodatna napomena ...'
                                                {...formik.getFieldProps('Napomena')}
                                                className={clsx(
                                                    controlClass,
                                                    'resize-none',
                                                    {'is-invalid': formik.touched.Napomena && formik.errors.Napomena},
                                                    {'is-valid': formik.touched.Napomena && !formik.errors.Napomena}
                                                )}
                                            />
                                            <FieldError name={'Napomena' as any} />
                                        </div>

                                        <div>
                                            <label className={clsx(labelClass, 'required')}>Status</label>
                                            <StatusIntervencijeDropdown
                                                value={formik.values.Status as any}
                                                onChange={(name, value) => formik.setFieldValue('Status', value)}
                                                errors={formik.errors.Status as any}
                                                touched={formik.touched.Status as any}
                                                selectName='Status'
                                            />
                                            <FieldError name={'Status' as any} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* BOX 4: RAZLOG KVARA */}
                        <div className={sectionCard}>
                            <div className='card-header border-0 py-4'>
                                <div className='card-title m-0 w-100'>
                                    <div className='d-flex align-items-start justify-content-between w-100 flex-column flex-md-row gap-2'>
                                        <div className='d-flex align-items-center gap-2'>
                                            <KTIcon iconName='information-5' className='fs-2 text-gray-700' />
                                            <h3 className='m-0 fs-4 fw-bold'>Razlog kvara</h3>
                                        </div>
                                        <div className={helpClass}>Odaberite razlog i po potrebi dopunite opis.</div>
                                    </div>
                                </div>
                            </div>

                            <div className={sectionBody}>
                                <div className='row g-6'>
                                    <div className='col-12 col-lg-4'>
                                        <label className={labelClass}>Razlog kvara</label>
                                        <select className={clsx(controlClass, 'form-select')} {...formik.getFieldProps('RazlogKvara')}>
                                            <option value=''>Odaberi...</option>
                                            <option value='Ljudski faktor'>Ljudski faktor</option>
                                            <option value='Dotrajalost'>Dotrajalost</option>
                                            <option value='Loše održavanje'>Loše održavanje</option>
                                            <option value='Ostalo'>Ostalo</option>
                                        </select>
                                        <FieldError name={'RazlogKvara' as any} />
                                    </div>

                                    <div className='col-12 col-lg-8'>
                                        <label className={labelClass}>
                                            Razlog kvara dodatno <span className={helpClass}>(ako je “Ostalo”, upišite detalje)</span>
                                        </label>
                                        <textarea
                                            rows={3}
                                            placeholder='Upišite dodatno objašnjenje...'
                                            {...formik.getFieldProps('RazlogKvaraDodatno')}
                                            className={clsx(
                                                controlClass,
                                                'resize-none',
                                                {
                                                    'is-invalid':
                                                        formik.touched.RazlogKvaraDodatno && formik.errors.RazlogKvaraDodatno,
                                                },
                                                {
                                                    'is-valid':
                                                        formik.touched.RazlogKvaraDodatno && !formik.errors.RazlogKvaraDodatno,
                                                }
                                            )}
                                        />
                                        <FieldError name={'RazlogKvaraDodatno' as any} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* BOX 5: SMJENA / UZROK */}
                        <div className={sectionCard}>
                            <div className='card-header border-0 py-4'>
                                <div className='card-title m-0 w-100'>
                                    <div className='d-flex align-items-start justify-content-between w-100 flex-column flex-md-row gap-2'>
                                        <div className='d-flex align-items-center gap-2'>
                                            <KTIcon iconName='calendar-8' className='fs-2 text-gray-700' />
                                            <h3 className='m-0 fs-4 fw-bold'>Smjena i uzrok</h3>
                                        </div>
                                        <div className={helpClass}>Dodatne informacije (opciono).</div>
                                    </div>
                                </div>
                            </div>

                            <div className={sectionBody}>
                                <div className='row g-6'>
                                    <div className='col-12 col-lg-6'>
                                        <label className={labelClass}>Smjena (paneli)</label>
                                        <SmjenePanelaDropdown
                                            value={formik.values.KvarNapravilaSmjena as any}
                                            onChange={(name, value) => formik.setFieldValue('KvarNapravilaSmjena', value)}
                                            errors={formik.errors.KvarNapravilaSmjena as any}
                                            touched={formik.touched.KvarNapravilaSmjena as any}
                                            selectName='KvarNapravilaSmjena'
                                        />
                                        <FieldError name={'KvarNapravilaSmjena' as any} />
                                    </div>

                                    <div className='col-12 col-lg-6'>
                                        <label className={labelClass}>Uzrokovao uposlenik</label>
                                        <UposlenikDropdown
                                            value={formik.values.KvarNapravioUposlenik as any}
                                            onChange={(name, value) => formik.setFieldValue('KvarNapravioUposlenik', value)}
                                            errors={formik.errors.KvarNapravioUposlenik as any}
                                            touched={formik.touched.KvarNapravioUposlenik as any}
                                            selectName='KvarNapravioUposlenik'
                                        />
                                        <FieldError name={'KvarNapravioUposlenik' as any} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FOOTER ACTIONS */}
                        <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 py-3'>
                            <div className='text-muted small'>
                                <KTIcon iconName='shield-tick' className='fs-5 me-2' />
                                Podaci se snimaju nakon klika na “Potvrdi”.
                            </div>

                            <button
                                type='submit'
                                className='btn btn-primary btn-lg px-8'
                                disabled={loading || formik.isSubmitting || !formik.isValid}
                            >
                                {!loading && (
                                    <>
                                        <KTIcon iconName='check' className='fs-3 me-2' />
                                        Potvrdi
                                    </>
                                )}

                                {loading && (
                                    <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

export default DodajIntervenciju
